// Site-wide image reveal: fade + slight zoom-out as each image scrolls in.
//
// WHY GSAP AND NOT THE LOAD EVENT
//
// Chrome deliberately starts fetching a loading="lazy" image well before it
// enters the viewport — a scroll margin of ~1200px on a fast connection, and
// it widens that margin on SLOW connections so the image is ready in time.
// (Measured on this site: fast net preloaded 2 off-screen images, throttled
// net preloaded 6, up to ~2500px past the fold.)
//
// So "image finished loading" is NOT "user can see the image", and no CSS or
// JS can tighten that margin. Animating on the load event therefore fires for
// off-screen images, and the user scrolls down to find them already revealed.
//
// Decoupling the two fixes it: the browser keeps its aggressive prefetch (good
// for performance) while ScrollTrigger owns the reveal at the real scroll
// position (good for the user). Lazy-loading still works exactly as before —
// we are not reimplementing it, only choosing when the image becomes visible.
//
// HIDING IS DONE FROM JS, NEVER FROM CSS
//
// A stylesheet rule like `img { opacity: 0 }` strands every image if the JS
// fails to run. Here gsap.set() applies the hidden state, so no-JS and
// broken-JS both leave images fully visible.

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Images already owned by another animation or that must never animate.
//   [data-hero-gallery] — hero-gallery.js runs its own opacity/transform timeline
//   .block__hero__media — hero-shapes.js fades the whole media block in after
//                         its shape wipes; a second opacity animation fights it
//   .nav__panel — hidden until opened; revealed by the nav's own transition
//   loading="eager"   — above the fold / LCP, must paint immediately
const EXCLUDE = [
    "[data-hero-gallery] img",
    ".block__hero__media img",
    ".nav__panel img",
    ".dropdown-menu img",
    'img[loading="eager"]',
    // FancyApps carousel owns its slides' visibility: it clones and repositions
    // them, so our inline opacity/visibility fights the library. In WebKit this
    // left slides stuck at opacity 0 (Chromium happened to clear them). The
    // carousel already animates its own transitions.
    ".f-carousel__slide img",
    ".f-carousel img",
    // GlideJS has the same problem, for the same reason: it clones slides for
    // its peek/loop behaviour and drives them with transforms, so a slide's
    // ScrollTrigger never fires the reveal and the image stays at autoAlpha 0.
    // Reported on slider markup in Safari; the Grid block in
    // carousel mode shares the markup and the same latent bug.
    ".glide__slide img",
    ".glide img",
].join(",");

function targets(root) {
    return [...root.querySelectorAll("img")].filter((img) => !img.matches(EXCLUDE));
}

// Is this element within (or above) the current viewport right now?
function inViewport(el) {
    const r = el.getBoundingClientRect();
    // Zero-sized elements (hidden containers) are not "in view".
    if (r.width === 0 && r.height === 0) return false;
    return r.top < window.innerHeight;
}

// initPage() kills every ScrollTrigger before re-running us. An image that
// already revealed must not be re-hidden on re-init (it would flash), and an
// image still waiting must get a fresh trigger. `revealed` tracks the former.
export function initImageReveal(root = document) {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    targets(root).forEach((img) => {
        if (prefersReduced || img.dataset.revealed === "true") {
            gsap.set(img, { autoAlpha: 1, scale: 1 });
            return;
        }

        // ALREADY ON SCREEN AT LOAD -> animate straight away, no ScrollTrigger.
        //
        // A trigger for an above-the-fold element resolves to scroll position 0.
        // Nothing ever scrolls *to* 0 from elsewhere on a fresh load, and with
        // `once: true` it is never re-evaluated — so those images stayed at
        // opacity 0 until the first scroll event nudged ScrollTrigger awake.
        // (Reproduced in both WebKit and Chromium: 3 above-fold images stuck
        // hidden until a 50px scroll released them.)
        //
        // ScrollTrigger is only the right tool for content that has yet to be
        // scrolled to; for what the user can already see, just play the tween.
        if (inViewport(img)) {
            gsap.set(img, { autoAlpha: 0, scale: 1.04 });
            gsap.to(img, {
                autoAlpha: 1,
                scale: 1,
                duration: 0.9,
                ease: "power2.out",
                onComplete: () => {
                    img.dataset.revealed = "true";
                },
            });
            return;
        }

        // Hidden state applied here, not in CSS — see note above.
        gsap.set(img, { autoAlpha: 0, scale: 1.04 });

        gsap.to(img, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.9,
            ease: "power2.out",
            onComplete: () => {
                img.dataset.revealed = "true";
            },
            scrollTrigger: {
                trigger: img,
                // Matches the house reveal pattern in utilities/animations.js.
                start: "top 90%",
                once: true,
            },
        });
    });
}
